import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import * as api from "../lib/api";
import { setCacheScope } from "../features/clients/lib/cache";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  user: api.AuthUser | null;
  status: AuthStatus;
  isMasterAdmin: boolean;
  isImpersonating: boolean;
  canManageUsers: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  assumeUser: (userId: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Provides authentication state and actions to the application.
 *
 * On mount it calls `/auth/me` to recover an existing session. Exposes
 * `login`, `logout`, and `refresh` plus derived flags (`isMasterAdmin`,
 * `canManageUsers`) used by the UI to gate privileged surfaces.
 *
 * @param {{ children: ReactNode }} props - Provider children.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<api.AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  const refresh = async (): Promise<void> => {
    try {
      const me = await api.getMe();
      // Before the user is rendered: directory caches are per-account, and this
      // is the only place that knows which account is live.
      setCacheScope(me.id);
      setUser(me);
      setStatus("authenticated");
    } catch {
      setCacheScope(null);
      setUser(null);
      setStatus("unauthenticated");
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    await api.login(email, password);
    await refresh();
  };

  const logout = async (): Promise<void> => {
    await api.logout();
    setCacheScope(null);
    setUser(null);
    setStatus("unauthenticated");
  };

  const assumeUser = async (userId: string): Promise<void> => {
    await api.startImpersonation(userId);
    window.location.assign("/");
  };

  const exitImpersonation = async (): Promise<void> => {
    await api.stopImpersonation();
    window.location.assign("/");
  };

  const value: AuthContextValue = {
    user,
    status,
    isMasterAdmin: user?.isMasterAdmin ?? false,
    isImpersonating: Boolean(user?.impersonatedBy),
    canManageUsers:
      user?.permissions?.includes("*") || user?.permissions?.includes("users.manage") || false,
    login,
    logout,
    refresh,
    assumeUser,
    exitImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Accesses the authentication context.
 *
 * @returns {AuthContextValue} Current auth state and actions.
 * @throws {Error} When used outside of {@link AuthProvider}.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
